import { Box, Drawer, DrawerBody, DrawerContent, DrawerOverlay, Flex, Text, useDisclosure } from 'design-system';
import type { ReactNode } from 'react';
import { FiCheck, FiChevronDown } from 'react-icons/fi';

export interface MobileSelectProps<T> {
  /** Small-caps label above the trigger, reused as the sheet title. */
  label: string;
  options: T[];
  optionKey: (option: T) => string;
  isSelected: (option: T) => boolean;
  onSelect: (option: T) => void;
  renderOption: (option: T) => ReactNode;
  /** Content of the closed trigger field. */
  renderValue: () => ReactNode;
}

// Mobile-first select: a compact trigger field that opens a bottom sheet —
// thumb-sized rows, a drag-handle affordance, tap-to-pick-and-dismiss. This is
// the page's replacement for desktop dropdown menus (tiny tap targets, hover
// styling, floating anchored popovers — all wrong on a phone).
export function MobileSelect<T>({
  label,
  options,
  optionKey,
  isSelected,
  onSelect,
  renderOption,
  renderValue,
}: MobileSelectProps<T>) {
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <Box>
      <Text fontSize='10.5px' fontWeight='semibold' letterSpacing='0.1em' color='gray.500' mb='4px'>
        {label}
      </Text>
      <Flex
        as='button'
        type='button'
        w='full'
        align='center'
        justify='space-between'
        bg='white'
        border='1px solid'
        borderColor='gray.200'
        borderRadius='10px'
        px='12px'
        minH='42px'
        onClick={onOpen}>
        <Box flex='1' minW='0' textAlign='start'>
          {renderValue()}
        </Box>
        <Box ml='8px' flexShrink={0}>
          <FiChevronDown color='var(--chakra-colors-gray-400)' size='16px' />
        </Box>
      </Flex>

      <Drawer isOpen={isOpen} onClose={onClose} placement='bottom' autoFocus={false}>
        <DrawerOverlay />
        <DrawerContent borderTopRadius='16px' maxH='75vh'>
          <Box w='36px' h='4px' bg='gray.200' borderRadius='full' mx='auto' mt='8px' />
          <Text
            fontSize='10.5px'
            fontWeight='semibold'
            letterSpacing='0.1em'
            color='gray.500'
            textAlign='center'
            mt='10px'
            mb='2px'>
            {label}
          </Text>
          <DrawerBody px='12px' pt='6px' pb='max(16px, env(safe-area-inset-bottom))'>
            <Box maxW='480px' mx='auto'>
              {options.map((option) => {
                const selected = isSelected(option);
                return (
                  <Flex
                    key={optionKey(option)}
                    as='button'
                    type='button'
                    w='full'
                    textAlign='start'
                    align='center'
                    px='12px'
                    py='12px'
                    mb='4px'
                    borderRadius='10px'
                    border='1px solid'
                    borderColor={selected ? 'red.100' : 'transparent'}
                    bg={selected ? 'red.50' : 'transparent'}
                    _hover={{ bg: selected ? 'red.50' : 'gray.50' }}
                    onClick={() => {
                      onSelect(option);
                      onClose();
                    }}>
                    <Box flex='1' minW='0'>
                      {renderOption(option)}
                    </Box>
                    {selected && (
                      <Box ml='10px' flexShrink={0}>
                        <FiCheck color='var(--chakra-colors-red-500)' size='16px' />
                      </Box>
                    )}
                  </Flex>
                );
              })}
            </Box>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
}
